'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { authService } from '@/lib/authService';
import {
  getPlaybookContext,
  getSuggestedPrompts,
  selectPlaybooks,
  type SupportPlaybook,
} from '@/lib/ai/supportPlaybooks';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SemanticContextResult {
  /** Combined context string from the API (training + marketing + semantic search) */
  context: string;
  /** Playbook instructions for the current context */
  playbookContext: string;
  /** Suggested quick-action prompts */
  suggestedPrompts: string[];
  /** Selected playbooks for the current context */
  playbooks: SupportPlaybook[];
  /** Whether the data is from the API (true) or fallback to local playbooks only (false) */
  hasApiContext: boolean;
  /** Fraud/risk detection warnings to surface in the UI */
  fraudWarnings: string[];
}

interface UseSemanticContextParams {
  /** Whether the user is unauthenticated (public mode) */
  isPublic?: boolean;
  /** Current module key (e.g., 'total-finance', 'total-support') */
  moduleKey?: string;
  /** Whether the user is in a training/demo session */
  isTrainingMode?: boolean;
  /** Whether the context is the support queue */
  isSupportContext?: boolean;
  /** The topic/user message to generate context for */
  topic?: string;
  /** Whether to include training config context */
  includeTrainingContext?: boolean;
  /** Whether to include marketing config context */
  includeMarketingContext?: boolean;
  /** Whether to include semantic search results */
  includeSemanticSearch?: boolean;
  /** Whether to auto-fetch when params change */
  enabled?: boolean;
  /** Risk flags from useRiskAssessment — used to generate fraud detection context */
  riskFlags?: Array<{ id: string; severity: string; title: string; detail: string; recommendation: string }>;
}

// ---------------------------------------------------------------------------
// Cache — prevents redundant API calls within the same session
// ---------------------------------------------------------------------------

interface CacheEntry {
  context: string;
  timestamp: number;
}

const contextCache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getCacheKey(params: UseSemanticContextParams): string {
  return JSON.stringify({
    isPublic: params.isPublic,
    moduleKey: params.moduleKey,
    topic: params.topic,
    includeTrainingContext: params.includeTrainingContext,
    includeMarketingContext: params.includeMarketingContext,
    includeSemanticSearch: params.includeSemanticSearch,
  });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSemanticContext({
  isPublic = false,
  moduleKey,
  isTrainingMode = false,
  isSupportContext = false,
  topic,
  includeTrainingContext = true,
  includeMarketingContext = true,
  includeSemanticSearch = true,
  enabled = true,
  riskFlags,
}: UseSemanticContextParams) {
  const [result, setResult] = useState<SemanticContextResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Compute fraud detection context from risk flags
  // Use stable empty array when no riskFlags provided to prevent infinite re-renders
  const fraudWarnings = useMemo(
    () => (riskFlags ?? []).map((f) => `[${f.severity.toUpperCase()}] ${f.title}: ${f.detail} → ${f.recommendation}`),
    [riskFlags],
  );

  // Always compute playbook context locally (no API needed)
  const playbookContext = useMemo(
    () => getPlaybookContext(isPublic, moduleKey, isTrainingMode, isSupportContext, topic),
    [isPublic, moduleKey, isTrainingMode, isSupportContext, topic],
  );
  const suggestedPrompts = useMemo(
    () => getSuggestedPrompts(isPublic, moduleKey, isSupportContext),
    [isPublic, moduleKey, isSupportContext],
  );
  const playbooks = useMemo(
    () => selectPlaybooks(isPublic, moduleKey, isTrainingMode, isSupportContext, topic),
    [isPublic, moduleKey, isTrainingMode, isSupportContext, topic],
  );

  const fetchContext = useCallback(async () => {
    if (!enabled) {
      setResult(null);
      return;
    }

    // For public users, we skip the API call (semantic-context requires auth)
    // and rely on local playbooks + marketing configs
    if (isPublic) {
      setResult({
        context: '',
        playbookContext,
        suggestedPrompts,
        playbooks,
        hasApiContext: false,
        fraudWarnings,
      });
      return;
    }

    // For authenticated users, fetch from the API
    const token = authService.getTokens()?.access_token;
    if (!token) {
      setResult({
        context: '',
        playbookContext,
        suggestedPrompts,
        playbooks,
        hasApiContext: false,
        fraudWarnings,
      });
      return;
    }

    // Check cache
    const cacheKey = getCacheKey({
      isPublic,
      moduleKey,
      topic,
      includeTrainingContext,
      includeMarketingContext,
      includeSemanticSearch,
    });

    const cached = contextCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      setResult({
        context: cached.context,
        playbookContext,
        suggestedPrompts,
        playbooks,
        hasApiContext: true,
        fraudWarnings,
      });
      return;
    }

    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/v1/ai/semantic-context', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          topic: topic || moduleKey || 'general assistance',
          moduleKey,
          includeTrainingContext,
          includeMarketingContext,
          includeSemanticSearch,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Semantic context API returned ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const apiContext = data.context || '';
        contextCache.set(cacheKey, { context: apiContext, timestamp: Date.now() });

        setResult({
          context: apiContext,
          playbookContext,
          suggestedPrompts,
          playbooks,
          hasApiContext: true,
          fraudWarnings,
        });
      } else {
        throw new Error(data.error || 'Failed to fetch semantic context');
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;

      console.error('[useSemanticContext] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch semantic context');

      // Fallback to local playbooks only
      setResult({
        context: '',
        playbookContext,
        suggestedPrompts,
        playbooks,
        hasApiContext: false,
        fraudWarnings,
      });
    } finally {
      setLoading(false);
    }
  }, [
    enabled,
    isPublic,
    moduleKey,
    topic,
    includeTrainingContext,
    includeMarketingContext,
    includeSemanticSearch,
    playbookContext,
    suggestedPrompts,
    fraudWarnings,
    playbooks,
  ]);

  useEffect(() => {
    fetchContext();
  }, [fetchContext]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    result,
    loading,
    error,
    refetch: fetchContext,
    // Convenience accessors
    context: result?.context || '',
    playbookContext,
    suggestedPrompts,
    playbooks,
    hasApiContext: result?.hasApiContext || false,
    fraudWarnings,
  };
}
